import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contasApi, transacoesApi, pluggyVinculosApi } from '../../api/endpoints'
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/EmptyState'
import { AccountTypeBadge, Badge, DonoBadge } from '../../components/ui/Badge'
import { Card, CardHeader, CardBody, AvatarIcon } from '../../components/ui/Card'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { AccountType, ACCOUNT_TYPE_LABEL } from '../../lib/enums'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { formatMoney, formatDateTime } from '../../lib/utils'
import type { FinancialAccount } from '../../types'
import { BancoLogo } from '../../components/BancoLogo'
import { SeletorBanco } from '../../components/SeletorBanco'
import { acharBanco } from '../../lib/bancos'
import { diaMes } from '../../lib/corte'
import { AvisoSaldoDivergente } from '../../components/AvisoSaldoDivergente'
import { usePluggyStatus } from '../pluggy/usePluggyStatus'
import { AvisoPluggyDesligada } from '../pluggy/AvisoPluggyDesligada'
import { SaldoAberturaModal } from './SaldoAberturaModal'

interface ContaForm {
  name: string
  institution: string
  type: AccountType
  balance: string
}

const EMPTY_FORM: ContaForm = { name: '', institution: '', type: AccountType.Checking, balance: '' }

function signedDiffLabel(diff: number) {
  return diff > 0 ? `+ ${formatMoney(diff)}` : `- ${formatMoney(Math.abs(diff))}`
}

/** Corrente, poupança e investimento rendem; cartão e dinheiro em espécie não. */
const PODE_RENDER: AccountType[] = [AccountType.Checking, AccountType.Savings, AccountType.Investment]

const hojeISO = () => new Date().toISOString().slice(0, 10)

export function ContasPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [busca, setBusca] = useState('')
  const [paraExcluir, setParaExcluir] = useState<FinancialAccount | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<FinancialAccount | null>(null)
  const [form, setForm] = useState<ContaForm>(EMPTY_FORM)
  const [erro, setErro] = useState<string | null>(null)
  const [definindoCorte, setDefinindoCorte] = useState<FinancialAccount | null>(null)
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  // Rendimento: o campo principal é o saldo que o banco mostra; o rendimento é a diferença para o saldo do Walleon.
  const [rendendo, setRendendo] = useState<FinancialAccount | null>(null)
  const [rendPorValor, setRendPorValor] = useState(false)
  const [rendTexto, setRendTexto] = useState('')
  const [rendData, setRendData] = useState(hojeISO())
  const [rendErro, setRendErro] = useState<string | null>(null)

  // Ajustar saldo: igual ao rendimento, mas vale pra qualquer tipo de conta e pros dois sentidos (sobe ou desce).
  const [ajustando, setAjustando] = useState<FinancialAccount | null>(null)
  const [ajustePorValor, setAjustePorValor] = useState(false)
  const [ajusteTexto, setAjusteTexto] = useState('')
  const [ajusteData, setAjusteData] = useState(hojeISO())
  const [ajusteErro, setAjusteErro] = useState<string | null>(null)

  const pluggy = usePluggyStatus()
  const { data: contas = [], isLoading, isError } = useQuery({
    queryKey: ['contas'],
    queryFn: () => contasApi.list(),
  })

  // Só consulta os vínculos se houver conta conectada; falha aqui não afeta a lista.
  const { data: vinculos = [] } = useQuery({
    queryKey: ['pluggy-vinculos'],
    queryFn: pluggyVinculosApi.list,
    enabled: contas.some((c) => c.isConnected),
  })

  const salvar = useMutation({
    mutationFn: () => {
      const base = { name: form.name.trim(), institution: form.institution.trim(), type: form.type }
      return editing ? contasApi.atualizar(editing.id, base) : contasApi.criar({ ...base, balance: Number(form.balance) || 0 })
    },
    onSuccess: (conta) => {
      qc.invalidateQueries({ queryKey: ['contas'] })
      qc.invalidateQueries({ queryKey: ['onboarding'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(editing ? 'Conta atualizada.' : 'Conta criada.')
      setModalOpen(false)
      // Primeira conta sem corte: segue direto para o saldo inicial e a data de corte (pode cancelar).
      if (!editing && !contas.some((c) => c.dataAbertura)) setDefinindoCorte(conta)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const rendNumero = Number(rendTexto.replace(',', '.')) || 0
  const rendimento = rendendo ? (rendPorValor ? rendNumero : Math.round((rendNumero - rendendo.balance) * 100) / 100) : 0
  const rendFutura = rendData > hojeISO()
  const rendAviso = !rendendo || rendTexto.trim() === '' || rendimento > 0 ? null
    : rendPorValor ? 'O rendimento deve ser maior que zero.'
    : rendimento < 0 ? `Esse valor é menor que o saldo atual no Walleon (${formatMoney(rendendo.balance)}). Lance o resgate ou corrija o saldo da conta antes.`
    : 'Sem rendimento novo: o saldo informado é igual ao do Walleon.'

  const registrarRendimento = useMutation({
    mutationFn: () => transacoesApi.registrarRendimento({
      accountId: rendendo!.id,
      ...(rendPorValor ? { valor: rendNumero } : { saldoAtual: rendNumero }),
      date: new Date(`${rendData}T00:00:00Z`).toISOString(),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contas'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      toast.success(`Rendimento de ${formatMoney(rendimento)} registrado.`)
      setRendendo(null)
    },
    onError: (e: Error) => setRendErro(e.message),
  })

  function openRendimento(conta: FinancialAccount) {
    setRendendo(conta)
    setRendPorValor(false)
    setRendTexto('')
    setRendData(hojeISO())
    setRendErro(null)
  }

  function openAjustar(conta: FinancialAccount) {
    setAjustando(conta)
    setAjustePorValor(false)
    setAjusteTexto('')
    setAjusteData(hojeISO())
    setAjusteErro(null)
  }

  // Atalhos vindos de "Primeiros passos": ?nova=1 abre o cadastro; ?saldoInicial=<id> abre o saldo inicial da conta.
  useEffect(() => {
    if (params.get('nova') === '1') { setParams({}, { replace: true }); openNew() }
    const id = params.get('saldoInicial')
    if (id) {
      const alvo = contas.find((c) => c.id === id)
      if (alvo) { setParams({}, { replace: true }); setDefinindoCorte(alvo) }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, contas])

  function openNew() {
    setEditing(null)
    setForm(EMPTY_FORM)
    setErro(null)
    setModalOpen(true)
  }

  function openEdit(conta: FinancialAccount) {
    setEditing(conta)
    setForm({ name: conta.name, institution: conta.institution, type: conta.type, balance: String(conta.balance) })
    setErro(null)
    setModalOpen(true)
  }

  function submit() {
    if (!form.name.trim() || !form.institution.trim()) {
      setErro('Informe o nome da conta e a instituição.')
      return
    }
    salvar.mutate()
  }

  const ajusteNumero = Number(ajusteTexto.replace(',', '.')) || 0
  const ajusteDiferenca = ajustando ? (ajustePorValor ? ajusteNumero : Math.round((ajusteNumero - ajustando.balance) * 100) / 100) : 0
  const ajusteFutura = ajusteData > hojeISO()

  const ajustarSaldo = useMutation({
    mutationFn: () => transacoesApi.ajustarSaldo({
      accountId: ajustando!.id,
      ...(ajustePorValor ? { valor: ajusteNumero } : { saldoAtual: ajusteNumero }),
      date: new Date(`${ajusteData}T00:00:00Z`).toISOString(),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contas'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      toast.success(`Saldo ajustado em ${signedDiffLabel(ajusteDiferenca)}.`)
      setAjustando(null)
    },
    onError: (e: Error) => setAjusteErro(e.message),
  })

  const remove = useMutation({
    mutationFn: (id: string) => contasApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contas'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success('Conta excluída.')
      setParaExcluir(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const termo = busca.toLowerCase()
  const filtered = contas.filter((c) => c.name.toLowerCase().includes(termo) || c.institution.toLowerCase().includes(termo))
  const saldoTotal = contas.reduce((s, c) => s + c.balance, 0)

  return (
    <>
      <PageHeader title="Contas" />

      {!pluggy.ativa && <AvisoPluggyDesligada mensagem={pluggy.mensagem} className="mb-4" />}

      <Card>
        <CardHeader
          title="Minhas contas"
          actions={
            <>
              <span className="text-13 text-muted">Saldo total <strong className="ml-1 font-semibold text-white">{formatMoney(saldoTotal)}</strong></span>
              <div className="w-60">
                <Input icon="ri-search-line" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar conta ou instituição..." aria-label="Buscar contas" />
              </div>
              <Button data-tutorial="conta.manual" icon="ri-add-line" onClick={openNew}>Nova conta</Button>
              {pluggy.ativa && <Button data-tutorial="conta.conectar" icon="ri-bank-line" onClick={() => navigate('/pluggy?conectar=1')}>Conectar banco (opcional)</Button>}
            </>
          }
        />
        <CardBody>
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState message="Erro ao carregar contas." />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="ri-bank-line"
              message={busca ? 'Nenhuma conta encontrada.' : 'Nenhuma conta cadastrada.'}
              action={!busca ? <Button variant="soft-primary" icon="ri-add-line" onClick={openNew}>Nova conta</Button> : undefined}
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {filtered.map((conta) => (
                <div key={conta.id} className="flex flex-col rounded border border-border/70 bg-surface2/40 p-4">
                  <div className="flex items-start justify-between gap-2">
                    {acharBanco(conta.institution) ? <BancoLogo nome={conta.institution} size={40} /> : <AvatarIcon icon="ri-bank-line" tone="info" />}
                    <div className="flex flex-wrap items-center gap-1">
                      <DonoBadge nome={conta.ownerNome} />
                      {conta.isConnected && <Badge variant="income">Conectada</Badge>}
                      <AccountTypeBadge type={conta.type} />
                    </div>
                  </div>
                  <h5 className="mt-3 truncate text-15 font-medium text-white">{conta.name}</h5>
                  <p className="truncate text-13 text-muted">{conta.institution}</p>
                  <p className="mt-3 text-2xs uppercase text-muted">Saldo atual</p>
                  <p className={`text-xl font-semibold ${conta.balance >= 0 ? 'text-white' : 'text-expense'}`}>{formatMoney(conta.balance)}</p>
                  {(() => {
                    const v = vinculos.find((x) => x.financialAccountId === conta.id && x.saldoDivergente)
                    return v?.saldoDivergente ? <AvisoSaldoDivergente divergencia={v.saldoDivergente} bancoAtualizadoEm={v.bancoAtualizadoEm} /> : null
                  })()}
                  {conta.dataAbertura && conta.saldoAbertura != null && (
                    <p className="mt-1 text-2xs text-muted">Saldo inicial em {diaMes(conta.dataAbertura)}: {formatMoney(conta.saldoAbertura)}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-2">
                    <span className="flex items-center gap-1 text-2xs text-muted">
                      <i className="ri-time-line" aria-hidden /> {formatDateTime(conta.updatedAt)}
                    </span>
                    <div className="flex">
                      {PODE_RENDER.includes(conta.type) && !conta.ownerNome && (
                        <IconButton icon="ri-line-chart-line" label={`Registrar rendimento de ${conta.name}`} tone="success" onClick={() => openRendimento(conta)} />
                      )}
                      {!conta.ownerNome && !conta.isConnected && (
                        <IconButton icon="ri-calendar-check-line" label={`Definir saldo inicial e corte de ${conta.name}`} tone="muted" onClick={() => setDefinindoCorte(conta)} />
                      )}
                      {!conta.ownerNome && (
                        <IconButton icon="ri-scales-3-line" label={`Ajustar saldo de ${conta.name}`} tone="muted" onClick={() => openAjustar(conta)} />
                      )}
                      <IconButton icon="ri-pencil-fill" label={`Editar ${conta.name}`} tone="info" onClick={() => openEdit(conta)} />
                      <IconButton icon="ri-delete-bin-5-line" label={`Excluir ${conta.name}`} tone="danger" onClick={() => setParaExcluir(conta)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar conta' : 'Nova conta'}
        footer={
          <>
            <Button variant="light" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button icon={editing ? 'ri-save-line' : 'ri-add-line'} onClick={submit} loading={salvar.isPending}>{editing ? 'Salvar' : 'Criar conta'}</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Nome da conta" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: Conta Corrente" autoFocus />
          <SeletorBanco label="Instituição" value={form.institution} onChange={(nome) => setForm((f) => ({ ...f, institution: nome }))} />
          <Select label="Tipo" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: Number(e.target.value) as AccountType }))}>
            {Object.entries(ACCOUNT_TYPE_LABEL).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
          </Select>
          {!editing && (
            <Input
              label="Saldo inicial (R$)"
              type="number" step="0.01"
              value={form.balance}
              onChange={(e) => setForm((f) => ({ ...f, balance: e.target.value }))}
              placeholder="0,00"
            />
          )}
          {editing && (
            <p className="text-13 text-muted sm:col-span-2">
              Saldo atual: <strong className="text-white">{formatMoney(editing.balance)}</strong> — pra corrigir, use "Ajustar saldo" na listagem.
            </p>
          )}
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense sm:col-span-2" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!rendendo}
        onClose={() => setRendendo(null)}
        title={`Rendimento · ${rendendo?.name ?? ''}`}
        footer={
          <>
            <Button variant="light" onClick={() => setRendendo(null)}>Cancelar</Button>
            <Button icon="ri-line-chart-line" onClick={() => registrarRendimento.mutate()} loading={registrarRendimento.isPending} disabled={rendTexto.trim() === '' || rendimento <= 0 || rendFutura}>Registrar rendimento</Button>
          </>
        }
      >
        <p className="mb-3 text-13 text-muted">Saldo atual no Walleon: <strong className="text-white">{formatMoney(rendendo?.balance ?? 0)}</strong></p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={rendPorValor ? 'Valor do rendimento (R$)' : 'Saldo atual no banco (R$)'}
            type="number" min={0} step="0.01" value={rendTexto} placeholder="0,00" autoFocus
            onChange={(e) => setRendTexto(e.target.value)}
          />
          <Input label="Data" type="date" max={hojeISO()} value={rendData} onChange={(e) => setRendData(e.target.value)} />
        </div>
        <button type="button" className="mt-2 text-13 text-primary hover:underline" onClick={() => { setRendPorValor((v) => !v); setRendTexto('') }}>
          {rendPorValor ? 'Informar o saldo atual do banco' : 'Informar o valor do rendimento'}
        </button>
        {rendimento > 0 && (
          <p className="mt-3 flex items-center justify-between rounded bg-income/10 px-3 py-2 text-income">
            <span className="text-13 font-medium">Rendimento</span>
            <span className="text-lg font-semibold">+ {formatMoney(rendimento)}</span>
          </p>
        )}
        {rendAviso && <p className="mt-3 text-13 text-expense" role="alert">{rendAviso}</p>}
        {rendFutura && <p className="mt-3 text-13 text-expense" role="alert">A data do rendimento não pode ser futura.</p>}
        {rendErro && <p className="mt-3 flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {rendErro}</p>}
      </Modal>

      <Modal
        open={!!ajustando}
        onClose={() => setAjustando(null)}
        title={`Ajustar saldo · ${ajustando?.name ?? ''}`}
        footer={
          <>
            <Button variant="light" onClick={() => setAjustando(null)}>Cancelar</Button>
            <Button icon="ri-scales-3-line" onClick={() => ajustarSaldo.mutate()} loading={ajustarSaldo.isPending} disabled={ajusteTexto.trim() === '' || ajusteDiferenca === 0 || ajusteFutura}>Ajustar saldo</Button>
          </>
        }
      >
        <p className="mb-3 text-13 text-muted">Saldo atual no Walleon: <strong className="text-white">{formatMoney(ajustando?.balance ?? 0)}</strong></p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={ajustePorValor ? 'Diferença a ajustar (R$, use negativo pra descer)' : 'Saldo real atual (R$)'}
            type="number" step="0.01" value={ajusteTexto} placeholder="0,00" autoFocus
            onChange={(e) => setAjusteTexto(e.target.value)}
          />
          <Input label="Data" type="date" max={hojeISO()} value={ajusteData} onChange={(e) => setAjusteData(e.target.value)} />
        </div>
        <button type="button" className="mt-2 text-13 text-primary hover:underline" onClick={() => { setAjustePorValor((v) => !v); setAjusteTexto('') }}>
          {ajustePorValor ? 'Informar o saldo real atual' : 'Informar a diferença direto'}
        </button>
        {ajusteTexto.trim() !== '' && ajusteDiferenca !== 0 && (
          <p className={`mt-3 flex items-center justify-between rounded px-3 py-2 ${ajusteDiferenca > 0 ? 'bg-income/10 text-income' : 'bg-expense/10 text-expense'}`}>
            <span className="text-13 font-medium">Ajuste</span>
            <span className="text-lg font-semibold">{signedDiffLabel(ajusteDiferenca)}</span>
          </p>
        )}
        {ajusteFutura && <p className="mt-3 text-13 text-expense" role="alert">A data do ajuste não pode ser futura.</p>}
        {ajusteErro && <p className="mt-3 flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {ajusteErro}</p>}
      </Modal>

      {definindoCorte && <SaldoAberturaModal conta={definindoCorte} onClose={() => setDefinindoCorte(null)} />}

      <ConfirmDialog
        open={!!paraExcluir}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remove.mutate(paraExcluir.id)}
        loading={remove.isPending}
        message={<>A conta <strong className="text-white">{paraExcluir?.name}</strong> será excluída.</>}
      />
    </>
  )
}
